import GlobalService from "@services/global-service";
import UrlService from "@services/url-service";
import Component from "@base/js/component";
import Drawer from "@components/drawer/drawer";
import Chips from "@components/chips/chips";
import { createFocusTrap } from "focus-trap";
import Calendar from "../calendar/calendar";

export default class SearchOverlay extends Component {
  // Defines the element selector which will initialize this component
  static selector = ".search-overlay";

  // Defines children that needs to be queried as part of this component
  static children = {
    close: ".search-overlay__close",
    form: "form",
    input: ".search-overlay__input",
    selectedFiltersSection: ".search-overlay__section--selected",
    clear: ".search-overlay__clear",
    filterGroups: [".js-search-overlay__group"],
  };

  // Defines sub components that needs to be queried as part of this component
  static members = {
    drawers: [Drawer],
    chips: Chips,
    calendar: Calendar,
  };

  // Will be changed with whatever is .search-overlay's data-search-page is
  searchPagePrefix = "/search";

  // Will store queried elements and data
  filterGroups = {
    // filteName: { items, allCheckbox }
  };

  // Defines event handlers to attach to children
  eventHandlers = {
    clear: {
      click: this.onClearClick,
    },
    close: {
      click: this.onCloseClick,
    },
  };

  get visible() {
    return this.element.classList.contains("search-overlay--visible");
  }

  // Will be called upon component instantiation
  afterInit() {
    Calendar.init();
    this.searchPagePrefix = this.element.dataset.searchPage;

    this.handleFilterGroups();
    this.handleChips();
    this.handleForm();

    // Move search overlay to the end of body
    document.body.appendChild(this.element);

    this.trap = createFocusTrap(".search-overlay", {
      onActivate: () => this.show(),

      // There is a delay between when the class is applied
      // and when the element is focusable
      checkCanFocusTrap: (trapContainers) => {
        const results = trapContainers.map(
          (trapContainer) =>
            new Promise((resolve) => {
              const interval = setInterval(() => {
                if (getComputedStyle(trapContainer).visibility !== "hidden") {
                  resolve();
                  clearInterval(interval);
                }
                this.show();
              }, 250);
            })
        );
        // Return a promise that resolves when all the trap containers are able to receive focus
        return Promise.all(results);
      },
      onDeactivate: () => this.hide(),
      initialFocus: this.children.input,
    });

    document
      .getElementById("primary-nav__search")
      .addEventListener("click", this.trap.activate);
    this.children.close.addEventListener("click", this.trap.deactivate);
  }

  handleFilterGroups() {
    this.children.filterGroups.forEach((filterGroup) => {
      const { filterName } = filterGroup.dataset;
      const filterHeading = filterGroup.querySelector(
        `.search-overlay__filter-heading`
      );
      const [...filterCheckboxes] = filterGroup.querySelectorAll(
        `input[type="checkbox"],input[type="radio"]`
      );

      // Save filterGroup elements and data
      this.filterGroups[filterName] = {
        title: filterHeading.textContent.trim(),
        items: [],
      };

      // Handle filter checkboxes
      filterCheckboxes.forEach((element) => {
        // Handle "all"
        if (/all/i.test(element.value)) {
          this.filterGroups[filterName].allCheckbox = element;
          element.addEventListener("change", () =>
            this.onAllCheckboxChange(element, filterName)
          );
        } else {
          this.filterGroups[filterName].items.push(element);
          element.addEventListener("change", () =>
            this.onFilterCheckboxChange(element, filterName)
          );
        }
      });
    });
  }

  handleChips() {
    // Use a marker for chips container
    this.chipsMarker = this.children.selectedFiltersSection.nextElementSibling;

    // Remove chips container initially
    this.children.selectedFiltersSection.parentElement.removeChild(
      this.children.selectedFiltersSection
    );

    // Chips event callbacks
    this.members.chips.onRemove(this.onChipRemove.bind(this));
    this.members.chips.onAdd(this.onChipAdd.bind(this));
  }

  handleForm() {
    this.children.form.addEventListener("submit", (e) => {
      e.preventDefault();

      // Prepare search url params
      const searchPArams = {
        [this.children.input.name]: this.children.input.value,
        ...this.getSelectedFilters(),
      };

      // Build redirect url to search page
      const filtersAsParams = UrlService.toSearchParamsString(searchPArams);
      const targetURL = `${this.searchPagePrefix}${filtersAsParams}`;

      // Redirect to search page
      window.location.href = targetURL;
    });
  }

  onFilterCheckboxChange(checkboxElement, filterName) {
    const { value, checked, type } = checkboxElement;
    let { textContent } = checkboxElement.parentElement.nextElementSibling;

    if (value === "date-specific-date") {
      const dateTextContent = checkboxElement
        .closest(".drawer")
        .querySelector(".search-overlay__filter-heading-text").textContent;
      textContent = dateTextContent;
    }

    if (checked) {
      this.members.chips.add({
        value,
        text: textContent.trim(),
        key: filterName,
        // Additional data can be saved in a chip
        filterName,
      });

      // Remove "all" if there is any
      const allFilter = this.members.chips.items.find(
        (chip) => /all/i.test(chip.value) && chip.key === filterName
      );

      if (allFilter) this.members.chips.remove(filterName, allFilter.value);
    } else {
      this.members.chips.remove(filterName, value);
    }

    // If radio
    if (type === "radio") {
      // Remove previously selected radio's chip
      this.members.chips.items.some(
        (chip) =>
          chip.value !== value &&
          chip.key === filterName &&
          this.members.chips.remove(filterName, chip.value)
      );
      this.element.querySelector(`.calendar`).style.display = "none";
      if (value === "date-specific-date") {
        this.element.querySelector(`.calendar`).style.display = "block";
      }
    }
  }

  onChipRemove(chipData) {
    // Once a chip is manually removed, we should also uncheck the filter
    if (chipData.filterName && chipData.filterName in this.filterGroups) {
      const filterGroup = this.filterGroups[chipData.filterName];

      const checkbox = [...filterGroup.items, filterGroup.allCheckbox].find(
        (_checkbox) => _checkbox.value === chipData.value
      );

      // Uncheck checkbox if any
      if (checkbox) {
        checkbox.checked = false;
      }
    }

    this.checkSelectedFiltersLength();
  }

  onChipAdd() {
    this.checkSelectedFiltersLength();
  }

  onAllCheckboxChange(checkboxElement, filterName) {
    if (filterName in this.filterGroups) {
      const { title } = this.filterGroups[filterName];

      if (checkboxElement.checked) {
        this.members.chips.add({
          value: checkboxElement.value,
          text: `All ${title}`,
          key: filterName,
          filterName,
        });

        // Remove all selected filters within the filterGroup
        [...this.members.chips.items].forEach((chip) => {
          if (
            chip.filterName === filterName &&
            chip.value !== checkboxElement.value
          ) {
            this.members.chips.remove(filterName, chip.value);
          }
        });
      } else {
        this.members.chips.remove(filterName, checkboxElement.value);
      }

      // If radio
      if (checkboxElement.type === "radio") {
        this.element.querySelector(`.calendar`).style.display = "none";
      }
    }
  }

  onClearClick(e) {
    e.preventDefault();
    [...this.members.chips.items].forEach((chip) =>
      this.members.chips.remove(chip.key, chip.value)
    );
  }

  onCloseClick(e) {
    e.preventDefault();
    this.hide();
  }

  checkSelectedFiltersLength() {
    if (this.members.chips.items.length) {
      this.showSelectedFilters();
    } else {
      this.hideSelectedFilters();
    }
  }

  showSelectedFilters() {
    this.chipsMarker.insertAdjacentElement(
      "beforebegin",
      this.children.selectedFiltersSection
    );
  }

  hideSelectedFilters() {
    this.children.selectedFiltersSection.parentElement.removeChild(
      this.children.selectedFiltersSection
    );
  }

  getSelectedFilters() {
    const selectedFilters = {};

    Object.values(this.filterGroups).forEach((filterGroup) => {
      const selectedFilterCheckboxes = filterGroup.items.filter(
        (element) => element.checked && element.value
      );

      if (selectedFilterCheckboxes.length) {
        selectedFilterCheckboxes.forEach((checkbox) => {
          if (checkbox.value) {
            if (!(checkbox.name in selectedFilters)) {
              selectedFilters[checkbox.name] = [];
            }

            selectedFilters[checkbox.name].push(...checkbox.value.split(","));
          }
        });
      }
    });

    if ("date_range" in selectedFilters) {
      if (selectedFilters.date_range.includes("date-specific-date")) {
        selectedFilters[this.members.calendar.children.fromRange.name] =
          this.members.calendar.children.fromRange.value.replaceAll("/", "%2F");
        selectedFilters[this.members.calendar.children.toRange.name] =
          this.members.calendar.children.toRange.value.replaceAll("/", "%2F");
      }
    }

    return selectedFilters;
  }

  show() {
    GlobalService.preventScroll();
    this.element.classList.add("search-overlay--visible");
    document.body.classList.add("is-search-overlay-visible");
  }

  hide() {
    GlobalService.allowScroll();
    this.element.classList.remove("search-overlay--visible");
    document.body.classList.remove("is-search-overlay-visible");
  }
}

// Remove this line if this component is not intended
// to initialize on DOM load.
SearchOverlay.init();
